import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  FileEdit,
  Plus,
  Play,
  Trash2,
  Info,
} from 'lucide-react';
import { useNotificationStore, type Notification } from '@/stores/notificationStore';
import { formatRelative } from '@/lib/format';

// =============================================================================
// NotificationPanel — dropdown anchored below the bell icon
// =============================================================================

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function groupByDay(items: Notification[]): { label: string; items: Notification[] }[] {
  const now = new Date();
  const todayStr = now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();

  const groups: Record<string, Notification[]> = {};
  const order: string[] = [];

  for (const item of items) {
    const d = new Date(item.timestamp);
    let label: string;
    if (d.toDateString() === todayStr) label = 'Today';
    else if (d.toDateString() === yesterdayStr) label = 'Yesterday';
    else label = 'Earlier';

    if (!groups[label]) {
      groups[label] = [];
      order.push(label);
    }
    groups[label].push(item);
  }

  return order.map((label) => ({ label, items: groups[label] }));
}

function typeIcon(type: Notification['type']) {
  switch (type) {
    case 'create':
      return <Plus className="h-3.5 w-3.5 text-emerald-400" />;
    case 'update':
      return <FileEdit className="h-3.5 w-3.5 text-blue-400" />;
    case 'delete':
      return <Trash2 className="h-3.5 w-3.5 text-red-400" />;
    case 'execute':
      return <Play className="h-3.5 w-3.5 text-amber-400" />;
    case 'system':
    default:
      return <Info className="h-3.5 w-3.5 text-surface-400" />;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { items, unreadCount, isLoading, markRead, markAllRead } = useNotificationStore();

  // Close on outside click
  useEffect(() => {
    if (!open) return;

    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    }

    // Defer listener so the opening click doesn't immediately close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [open, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const groups = groupByDay(items);

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-[calc(100vw-1.5rem)] sm:w-96 max-h-[28rem] bg-surface-800 border border-surface-700 rounded-xl shadow-xl shadow-black/30 flex flex-col z-50 overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-surface-700">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-surface-400" />
          <span className="text-sm font-semibold text-surface-200">Notifications</span>
          {unreadCount > 0 && (
            <span className="ml-1 rounded-full bg-blue-600 px-1.5 py-0.5 text-[10px] font-bold text-white leading-none">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllRead}
            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
          >
            <CheckCheck className="h-3 w-3" />
            Mark all read
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {isLoading && items.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-5 w-5 border-2 border-surface-600 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-surface-500">
            <Bell className="h-6 w-6 mb-2" />
            <span className="text-sm">No notifications yet</span>
          </div>
        ) : (
          groups.map((group) => (
            <div key={group.label}>
              <div className="sticky top-0 bg-surface-800/90 backdrop-blur-sm px-4 py-1.5 border-b border-surface-700/50">
                <span className="text-[11px] font-medium uppercase tracking-wider text-surface-500">
                  {group.label}
                </span>
              </div>
              {group.items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  className="w-full flex items-start gap-3 px-4 py-3 hover:bg-surface-700/50 transition-colors text-left"
                >
                  {/* Unread dot */}
                  <div className="mt-1.5 flex-shrink-0 w-2 h-2">
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                    )}
                  </div>

                  {/* Icon */}
                  <div className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-md bg-surface-700 flex items-center justify-center">
                    {typeIcon(n.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-medium truncate ${n.read ? 'text-surface-400' : 'text-surface-200'}`}>
                        {n.title}
                      </span>
                      <span className="flex-shrink-0 text-[11px] text-surface-500">
                        {formatRelative(n.timestamp)}
                      </span>
                    </div>
                    <p className="text-xs text-surface-500 truncate mt-0.5">{n.description}</p>
                  </div>

                  {/* Read indicator */}
                  {n.read && (
                    <Check className="mt-1 h-3 w-3 text-surface-600 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          ))
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-surface-700 px-4 py-2.5">
        <button
          onClick={() => {
            onClose();
            navigate('/audit');
          }}
          className="w-full text-center text-xs text-blue-400 hover:text-blue-300 transition-colors"
        >
          View all activity
        </button>
      </div>
    </div>
  );
}
